/**
 * CompanyDetailModal
 * Read-only side-sheet showing all relevant information for a given company:
 *  - Company ID and basic info
 *  - Associated owner (dueño)
 *  - Branches registered under the company
 */

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building2,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Hash,
  User as UserIcon,
  AtSign,
  Mail,
  Store,
} from "lucide-react";
import { type Company } from "@/services/companiesService";
import UserAvatar from "@/components/UserAvatar";
import { getBranches, type Branch } from "@/services/branchesService";
import { type User } from "@/services/usersService";

interface CompanyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
  /** Pre-resolved owner — find it in the parent with users.find(...) */
  owner: User | null;
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-white/40">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white/40 mb-0.5">{label}</p>
        <p className="text-sm text-white break-words">
          {value || <span className="text-white/30 italic">Sin dato</span>}
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
      {children}
    </p>
  );
}

export default function CompanyDetailModal({
  isOpen,
  onClose,
  company,
  owner,
}: CompanyDetailModalProps) {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !company) {
      setBranches([]);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const branchesData = await getBranches(company.id);
        setBranches(branchesData);
      } catch (err) {
        console.error("Error loading company branches:", err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [isOpen, company]);

  if (!company) return null;

  const createdAt = company.created_at
    ? new Date(company.created_at).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white flex-shrink-0">
              <Building2 className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {company.name}
              </SheetTitle>
              <div className="mt-2">
                <Badge
                  className={
                    company.is_active
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      : "bg-red-500/20 text-red-400 border-red-500/30"
                  }
                >
                  {company.is_active ? "Activa" : "Inactiva"}
                </Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Scrollable body */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Empresa — datos básicos */}
            <section className="space-y-3">
              <SectionTitle>
                <Building2 className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Información de la empresa
              </SectionTitle>

              <InfoRow
                icon={<Hash className="h-4 w-4" />}
                label="ID"
                value={<span className="font-mono text-xs">{company.id}</span>}
              />
              <InfoRow
                icon={<CalendarDays className="h-4 w-4" />}
                label="Registrada el"
                value={createdAt}
              />
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-white/40">
                  {company.is_active ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Estado</p>
                  <Badge
                    className={
                      company.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }
                  >
                    {company.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              </div>
            </section>

            <Separator className="bg-white/10" />

            {/* Dueño asociado */}
            <section className="space-y-3">
              <SectionTitle>
                <UserIcon className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Dueño asociado
              </SectionTitle>

              {owner ? (
                <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10 space-y-3">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      initials={(((owner.first_name?.[0] ?? '') + (owner.last_name?.[0] ?? '')).toUpperCase()) || owner.username[0].toUpperCase()}
                      role="owner"
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {[owner.first_name, owner.last_name].filter(Boolean).join(" ") || owner.username}
                      </p>
                      <Badge
                        className={
                          owner.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mt-1"
                            : "bg-red-500/20 text-red-400 border-red-500/30 mt-1"
                        }
                      >
                        {owner.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                  </div>
                  <InfoRow
                    icon={<AtSign className="h-4 w-4" />}
                    label="Usuario"
                    value={owner.username}
                  />
                  <InfoRow
                    icon={<Mail className="h-4 w-4" />}
                    label="Correo"
                    value={owner.email}
                  />
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin dueño asignado</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Sucursales */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <SectionTitle>
                  <Briefcase className="inline-block h-3 w-3 mr-1 mb-0.5" />
                  Sucursales
                </SectionTitle>
                {!isLoading && branches.length > 0 && (
                  <span className="text-xs text-white/40 -mt-3">
                    {branches.length} registradas
                  </span>
                )}
              </div>

              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-xl bg-white/10" />
                  ))}
                </div>
              ) : branches.length === 0 ? (
                <p className="text-sm text-white/30 italic">Sin sucursales registradas</p>
              ) : (
                <div className="space-y-2">
                  {branches.map((branch) => (
                    <div
                      key={branch.id}
                      className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 border border-white/10"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <UserAvatar role="branch" size="sm" icon={<Store className="h-4 w-4" />} />
                        <p className="text-sm text-white font-medium truncate">{branch.name}</p>
                      </div>
                      <Badge
                        className={
                          branch.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 ml-2 flex-shrink-0"
                            : "bg-red-500/20 text-red-400 border-red-500/30 ml-2 flex-shrink-0"
                        }
                      >
                        {branch.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </section>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
